import React from 'react'
import Button from '@mui/material/Button'
import makeStyles from '@mui/styles/makeStyles'

import clsx from 'clsx'
import CircularProgress from '@mui/material/CircularProgress'

import { useLocation, useNavigate } from 'react-router-dom'
import styles from './_styles/saveButtonsStyles'
import { displaySnackbar } from '../../actions/main'
import TokTokDocDialog from './Dialog'
import { useButtonsStyles } from '../../styles'
import { useAppDispatch } from '../../store'
import Prompt from './Prompt'
import { TokTokDocSaveButtonsProps } from './_props/TokTokDocSaveButtonsProps'
import useUserPermissions from '../../hooks/useUserPermissions'
import DeletionBannerDefaultContent from './DeletionBanner/DeletionBannerDefaultContent'

// @ts-expect-error TS(2345) FIXME: Argument of type '(theme: any) => { buttonsContain... Remove this comment to see the full error message
const useStyles = makeStyles(styles)

const TokTokDocSaveButtons: React.FC<TokTokDocSaveButtonsProps> = ({
    edition,
    allowedRoles,
    redirectTo = '',
    cancelRedirectTo = '',
    allowCancel,
    deleteActionMessage = 'Êtes-vous certain de vouloir supprimer ce téléconsultant ?',
    onSave,
    shouldBlockNavigation,
    allowDelete = true,
    isDeleteEnabled = true,
    isExternal,
    onDelete,
    isSaveEnabled = true,
}) => {
    const { hasUserPermission } = useUserPermissions()
    const classes = useStyles()
    const buttonsClasses = useButtonsStyles()
    const [loading, setLoading] = React.useState(false)
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const [open, setOpen] = React.useState(false)
    const [leaving, setLeaving] = React.useState(false)
    const [cancelling, setCancelling] = React.useState(false)

    const location = useLocation()

    const isDisabled = React.useMemo(() => {
        const isSaveEnabledResult = typeof isSaveEnabled === 'boolean' ? isSaveEnabled : isSaveEnabled()

        return loading || !isSaveEnabledResult
    }, [isSaveEnabled, loading])

    React.useEffect(() => {
        if (!leaving) return
        if (isExternal) return
        if (redirectTo) {
            navigate({
                pathname: redirectTo,
            })
        }
    }, [navigate, leaving, redirectTo, isExternal])

    React.useEffect(() => {
        if (!cancelling) return
        if (isExternal) return
        if (cancelRedirectTo) {
            navigate({
                pathname: cancelRedirectTo,
            })
        } else if (redirectTo) {
            navigate({
                pathname: redirectTo,
            })
        }
    }, [cancelling, isExternal, cancelRedirectTo, navigate, redirectTo])

    const leaveForm = () => {
        setLeaving(true)
    }

    const handleOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    const handleExternal = (action: any, id: any) => {
        const jsonAction = JSON.stringify({
            action,
            id,
        })

        // @ts-expect-error TS(2339) FIXME: Property 'ReactNativeWebView' does not exist on ty... Remove this comment to see the full error message
        if (window.ReactNativeWebView) {
            // @ts-expect-error TS(2339) FIXME: Property 'ReactNativeWebView' does not exist on ty... Remove this comment to see the full error message
            window.ReactNativeWebView.postMessage(jsonAction)
        } else {
            window.parent.postMessage(jsonAction, '*')
        }
    }

    const handleCancel = () => {
        // @ts-expect-error TS(2554) FIXME: Expected 2 arguments, but got 1.
        if (isExternal) handleExternal('cancel')
        else setCancelling(true)
    }

    const handleSave = () => {
        setLoading(true)
        if (allowedRoles && !hasUserPermission(allowedRoles)) {
            dispatch(
                displaySnackbar(
                    'error',
                    'Vous ne disposez pas des droits suffisants pour effectuer cette opération !',
                    undefined,
                    `[SaveButtons] sauvegarde - droits insuffisants - ressource ${location.pathname}`,
                ),
            )
            setLoading(false)
            return
        }
        if (onSave) {
            onSave()
                .then((result: any) => {
                    if (isExternal) {
                        handleExternal('save', result.id)
                    } else {
                        dispatch(
                            displaySnackbar(
                                'success',
                                result?.message ?? result,
                                undefined,
                                `[SaveButtons] sauvegarde succès - ressource ${location.pathname}`,
                            ),
                        )
                        setLoading(false)
                        leaveForm()
                    }
                })
                .catch((errorMsg: any) => {
                    setLoading(false)
                    if (errorMsg !== '')
                        dispatch(displaySnackbar('error', errorMsg?.message ? errorMsg.message : errorMsg.toString()))
                })
        }
    }

    const handleDelete = () => {
        setLoading(true)
        if (allowedRoles && !hasUserPermission(allowedRoles)) {
            dispatch(
                displaySnackbar(
                    'error',
                    'Vous ne disposez pas des droits suffisants pour effectuer cette opération !',
                    undefined,
                    `[SaveButtons] suppression - droits insuffisants - ressource ${location.pathname}`,
                ),
            )
            setLoading(false)
            return
        }
        if (onDelete) {
            onDelete()
                .then((result: any) => {
                    if (isExternal) handleExternal('delete', result.id)
                    dispatch(displaySnackbar('success', result?.message ?? result))
                    setLoading(false)
                    setOpen(false)
                    leaveForm()
                })
                .catch((errorMsg: any) => {
                    setLoading(false)
                    setOpen(false)
                    if (errorMsg !== '') dispatch(displaySnackbar('error', errorMsg))
                })
        }
    }

    const handleBlockedNavigation = () => {
        if (typeof shouldBlockNavigation !== 'undefined' && shouldBlockNavigation() && !leaving && !cancelling) {
            dispatch(
                displaySnackbar(
                    'warning',
                    'Vous avez effectué des modifications que vous n\'avez pas encore enregistrées. Cliquez sur le bouton "Enregistrer" ou "Annuler" du formulaire afin de poursuivre votre navigation.',
                ),
            )
            return false
        }
        return true
    }

    const renderDeleteButton = () => (
        <>
            <Button
                data-cy="openModalDelete"
                disableElevation
                className={clsx(buttonsClasses.Button, buttonsClasses.ButtonDeleteLight, buttonsClasses.ButtonMinWidth)}
                color="inherit"
                onClick={() => handleOpen()}
                disabled={!isDeleteEnabled}
            >
                Supprimer
            </Button>
            <TokTokDocDialog
                isOpen={open}
                loading={loading}
                handleClose={handleClose}
                handleSave={handleDelete}
                saveTitle="Supprimer"
                title={deleteActionMessage}
                subtitle="Après confirmation, la suppression sera définitive"
                isDeleteAction
                disableEscape
            >
                <DeletionBannerDefaultContent />
            </TokTokDocDialog>
        </>
    )

    const renderCancelButton = () => (
        <Button
            data-cy="cancelForm"
            disableElevation
            onClick={() => handleCancel()}
            color="inherit"
            className={clsx(
                buttonsClasses.Button,
                !allowDelete ? buttonsClasses.ButtonDeleteLight : buttonsClasses.ButtonCancel,
                buttonsClasses.ButtonMinWidth,
            )}
        >
            Annuler
        </Button>
    )

    const renderSaveButton = () => (
        <Button
            data-cy="save"
            disableElevation
            variant="contained"
            color="primary"
            disabled={isDisabled}
            className={clsx(buttonsClasses.Button, buttonsClasses.ButtonMinWidth)}
            onClick={() => handleSave()}
        >
            {/* @ts-expect-error TS(2339) FIXME: Property 'buttonProgress' does not exist on type '... Remove this comment to see the full error message */}
            {loading ? <CircularProgress size={24} className={classes.buttonProgress} /> : 'Enregistrer'}
        </Button>
    )

    return (
        <div className={classes.buttonsContainer}>
            <Prompt
                when={shouldBlockNavigation && shouldBlockNavigation() && !leaving && !cancelling}
                message={handleBlockedNavigation}
            />
            <div className={classes.buttonsContainerButtons}>
                {edition && allowDelete && renderDeleteButton()}
                {(edition || allowCancel) && renderCancelButton()}
                {onSave && renderSaveButton()}
            </div>
        </div>
    )
}

export default TokTokDocSaveButtons
